// frontend/src/contexts/ConfirmContext.tsx
/**
 * Confirm Context - Provides app-styled confirmation and info dialogs
 * Replaces window.confirm() and window.alert() with consistent modal UI
 *
 * Usage:
 *   const { confirm, info } = useConfirm();
 *
 *   // Confirmation dialog
 *   const confirmed = await confirm({
 *     title: 'Delete Item',
 *     message: 'Are you sure?',
 *     confirmText: 'Delete',
 *     variant: 'danger'
 *   });
 *   if (confirmed) { ... }
 *
 *   // Info dialog
 *   await info({
 *     title: 'About',
 *     content: <div>Some content here</div>
 *   });
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { ConfirmModal, InfoModal } from '../components/shared/modals';

// =============================================================================
// TYPES
// =============================================================================

export type ConfirmVariant = 'default' | 'danger' | 'warning';

export interface ConfirmOptions {
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmVariant;
}

export interface InfoOptions {
  title?: string;
  content?: ReactNode;
  closeText?: string;
}

export interface ConfirmContextValue {
  confirm: (options?: ConfirmOptions) => Promise<boolean>;
  info: (options?: InfoOptions) => Promise<void>;
}

interface ConfirmState {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  variant: ConfirmVariant;
  resolve: ((value: boolean) => void) | null;
}

interface InfoState {
  isOpen: boolean;
  title: string;
  content: ReactNode;
  closeText: string;
  resolve: (() => void) | null;
}

// =============================================================================
// CONTEXT
// =============================================================================

const ConfirmContext = createContext<ConfirmContextValue | null>(null);

export const useConfirm = (): ConfirmContextValue => {
  const context = useContext(ConfirmContext);
  if (!context) {
    throw new Error('useConfirm must be used within a ConfirmProvider');
  }
  return context;
};

// =============================================================================
// PROVIDER
// =============================================================================

interface ConfirmProviderProps {
  children: ReactNode;
}

export const ConfirmProvider = ({ children }: ConfirmProviderProps) => {
  // Confirm modal state
  const [confirmState, setConfirmState] = useState<ConfirmState>({
    isOpen: false,
    title: 'Confirm',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    variant: 'default',
    resolve: null
  });

  // Info modal state
  const [infoState, setInfoState] = useState<InfoState>({
    isOpen: false,
    title: 'Information',
    content: null,
    closeText: 'Close',
    resolve: null
  });

  const confirm = useCallback((options: ConfirmOptions = {}): Promise<boolean> => {
    const {
      title = 'Confirm',
      message = 'Are you sure?',
      confirmText = 'Confirm',
      cancelText = 'Cancel',
      variant = 'default'
    } = options;

    return new Promise((resolve) => {
      setConfirmState({
        isOpen: true,
        title,
        message,
        confirmText,
        cancelText,
        variant,
        resolve
      });
    });
  }, []);

  const info = useCallback((options: InfoOptions = {}): Promise<void> => {
    const {
      title = 'Information',
      content,
      closeText = 'Close'
    } = options;

    return new Promise((resolve) => {
      setInfoState({
        isOpen: true,
        title,
        content,
        closeText,
        resolve
      });
    });
  }, []);

  const handleConfirmClose = useCallback(() => {
    setConfirmState(prev => {
      if (prev.resolve) {
        prev.resolve(false);
      }
      return { ...prev, isOpen: false, resolve: null };
    });
  }, []);

  const handleConfirmConfirm = useCallback(() => {
    setConfirmState(prev => {
      if (prev.resolve) {
        prev.resolve(true);
      }
      return { ...prev, isOpen: false, resolve: null };
    });
  }, []);

  const handleInfoClose = useCallback(() => {
    setInfoState(prev => {
      if (prev.resolve) {
        prev.resolve();
      }
      return { ...prev, isOpen: false, resolve: null };
    });
  }, []);

  const value: ConfirmContextValue = { confirm, info };

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <ConfirmModal
        isOpen={confirmState.isOpen}
        onClose={handleConfirmClose}
        onConfirm={handleConfirmConfirm}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
        variant={confirmState.variant}
      />
      <InfoModal
        isOpen={infoState.isOpen}
        onClose={handleInfoClose}
        title={infoState.title}
        closeText={infoState.closeText}
      >
        {infoState.content}
      </InfoModal>
    </ConfirmContext.Provider>
  );
};

export default ConfirmContext;
